import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, ErrorHandler, Inject, Injector, OnDestroy, OnInit, ViewChild, inject, signal } from '@angular/core'
import { form, required } from '@angular/forms/signals'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'

import { BicService } from '@lemon/core/service/bic.service'
import { KayttajaService } from '../../_angular/service/kayttaja.service'
import { LemonTranslationService } from '../../_jaettu-angular/service/lemon-translation.service'

import { DialogToShowMetadata } from '@lemon/core-firebase/model/banks'
import { EnableBankingAspspList, EnableBankingAspspSubtype, PSUType } from '@lemon/core-firebase/model/enable-banking-shared'
import { DateService } from '@lemon/core/service/date.service'
import { IbanService } from '@lemon/core/service/iban.service'
import { StringService } from '@lemon/core/service/string.service'
import { EnableBankingDialogService, ViewableEnableBankingAspspSubtype } from 'app/_angular/service/enable-banking-dialog.service'
import { arvonMuutokset, asetaArvo, nollaa } from 'app/_jaettu-angular/_lomakkeet/arvon-muutokset'
import { LadataanService } from 'app/_jaettu-angular/service/ladataan.service'
import { Observable, Subject } from 'rxjs'
import { map, takeUntil } from 'rxjs/operators'

export interface EnableBankingDialogData {
  dialogToShowMetadata: DialogToShowMetadata
  /** Esiladattu ASPSP-lista — kutsujan vastuulla hakea ennen avaamista, jotta dialogin renderöinti pysyy synkronisena. */
  bankSubtypes: EnableBankingAspspList['bankSubtypes']
}

interface PankkityyppiLomake {
  ebSubtypeName: string | null
  ebPsuType: PSUType | null
}

interface Account {
  pankki: string
  bic: string
  iban: string
  nimi?: string
}

@Component({
  templateUrl: './enable-banking-login-required.dialog.html',
  styleUrls: ['./enable-banking-login.dialog.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class EnableBankingLoginRequiredDialog implements OnInit, OnDestroy {
  @ViewChild('selitys', { static: true }) selitysRef: ElementRef

  nonAccessibleIbansObservable: Observable<Account[]>
  accessibleIbansObservable: Observable<Account[]>
  valtuutusTextsObservable: Observable<string>

  valtuutusTitle: string
  valtuutusStartText: string
  valtuutusEndText: string
  lueLisaa: string

  commonError: string
  extraTextExpanded: boolean = false

  /**
   * True when the dialog metadata is missing the original (name, psuType) — i.e. the customer has
   * never authorized for this maksutapa. In that case the dialog renders an inline picker.
   */
  requiresPicker: boolean = false
  currentEbSubtypes: EnableBankingAspspSubtype[] = null
  readonly selectedEbSubtype = signal<ViewableEnableBankingAspspSubtype>(null)

  lomake = form(signal<PankkityyppiLomake>({ ebSubtypeName: null, ebPsuType: null }), polku => {
    required(polku.ebSubtypeName)
    required(polku.ebPsuType)
  })

  private _buttonWasPushed: boolean
  private _ngUnsubscribe = new Subject<void>()
  private readonly _injector = inject(Injector)

  constructor(
    @Inject(MAT_DIALOG_DATA) private _data: EnableBankingDialogData,
    private _dialogRef: MatDialogRef<EnableBankingLoginRequiredDialog>,
    private _translationService: LemonTranslationService,
    private _kayttajaService: KayttajaService,
    private _bicService: BicService,
    private _stringService: StringService,
    private _ibanService: IbanService,
    private _errorHandler: ErrorHandler,
    private _ladataanService: LadataanService,
    private _cd: ChangeDetectorRef,
    private _enableBankingDialogService: EnableBankingDialogService,
    private _dateService: DateService,
  ) {
    this._initializeTexts()
  }

  ngOnInit() {
    this._dialogRef.disableClose = true

    arvonMuutokset(this.lomake.ebSubtypeName, { injector: this._injector })
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(subtypeName => {
        if (subtypeName) {
          this.selectSubtype(subtypeName)
        } else {
          this.selectedEbSubtype.set(null)
          nollaa(this.lomake.ebPsuType, null)
        }
      })

    const aspspName = this._data?.dialogToShowMetadata?.providerMetadata?.enableBankingMetadata?.aspsp?.name
    const psuType = this._data?.dialogToShowMetadata?.providerMetadata?.enableBankingMetadata?.psuType
    this.requiresPicker = !aspspName || !psuType
    if (this.requiresPicker) {
      this._initializePickerSubtypes()
    }

    this.accessibleIbansObservable = this._kayttajaService.nykyinenAsiakasObservable.pipe(
      map(asiakas => {
        if (!asiakas) {
          return null
        }

        const ibans = this._data?.dialogToShowMetadata?.ibansOk
        if (!ibans?.length) {
          return null
        }
        return ibans.map(iban => {
          const noWhitespaceIban = this._stringService.removeAllWhiteSpaces(iban) || ''
          const pankki = this._bicService.annaPankinNimi(noWhitespaceIban) || ''
          const bic = this._bicService.annaBicKoodi(noWhitespaceIban) || ''
          return {
            pankki: pankki,
            bic: bic,
            iban: this._ibanService.formatoiIban(noWhitespaceIban),
          }
        })
      }),
    )

    this.nonAccessibleIbansObservable = this._kayttajaService.nykyinenAsiakasObservable.pipe(
      map(asiakas => {
        if (!asiakas) {
          return null
        }

        const ibansMissing = this._data?.dialogToShowMetadata?.ibansMissing

        if (!ibansMissing?.length) {
          return null
        }
        return ibansMissing.map(iban => {
          const noWhitespaceIban = this._stringService.removeAllWhiteSpaces(iban) || ''
          const pankki = this._bicService.annaPankinNimi(noWhitespaceIban) || ''
          const bic = this._bicService.annaBicKoodi(noWhitespaceIban) || ''
          return {
            pankki: pankki,
            bic: bic,
            iban: this._ibanService.formatoiIban(noWhitespaceIban),
          }
        })
      }),
    )
  }

  selectSubtype(subtypeName: string) {
    const subtype = this.currentEbSubtypes?.find(s => s.name === subtypeName)
    if (!subtype) {
      this._errorHandler.handleError(new Error('Unknown EB subtype selected: ' + subtypeName))
      return
    }
    this.selectedEbSubtype.set(this._enableBankingDialogService.convertToSelectedEbSubtype(subtype))
    // Reset PSU type when the subtype changes — its psuTypes set may differ.
    nollaa(this.lomake.ebPsuType, null)
    if (subtype.psuTypes.length === 1) {
      asetaArvo(this.lomake.ebPsuType, subtype.psuTypes[0])
    }
  }

  isJatkaDisabled(): boolean {
    if (!this.requiresPicker) {
      return false
    }
    return !this.selectedEbSubtype() || !this.lomake.ebPsuType().value()
  }

  async goToEnableBanking() {
    if (this._buttonWasPushed) {
      return
    }
    this._buttonWasPushed = true

    this._ladataanService.aloitaLataaminen()

    try {
      const tiedot = await this._kayttajaService.getKayttaja()
      const firstMissingIban = this._data.dialogToShowMetadata.ibansMissing?.[0]

      if (!firstMissingIban) {
        throw new Error('No IBAN available to connect')
      }

      let aspspName: string
      let psuType: PSUType
      if (this.requiresPicker) {
        if (!this.selectedEbSubtype()) {
          throw new Error('No ASPSP subtype selected')
        }
        aspspName = this.selectedEbSubtype().name
        psuType = this.lomake.ebPsuType().value()
        if (!psuType) {
          throw new Error('No PSU type selected')
        }
      } else {
        aspspName = this._data.dialogToShowMetadata.providerMetadata?.enableBankingMetadata?.aspsp?.name
        psuType = this._data.dialogToShowMetadata.providerMetadata?.enableBankingMetadata?.psuType
        if (!aspspName || !psuType) {
          throw new Error('Missing enableBankingMetadata.aspsp.name or enableBankingMetadata.psuType in dialog metadata')
        }
      }

      const redirectUrl = await this._enableBankingDialogService.fetchEnableBankingOauthStartUrl(tiedot.asiakasAvain, aspspName, psuType, tiedot.asiointikieli, firstMissingIban)

      if (!redirectUrl) {
        throw new Error('No redirectUrl to connect to Enable Banking')
      }
      location.href = redirectUrl
    } catch (err) {
      this._errorHandler.handleError(err)
    } finally {
      this._buttonWasPushed = false
      this._ladataanService.lopetaLataaminen()
    }
  }

  async postpone() {
    this._enableBankingDialogService.postpone()
    this._dialogRef.close()
  }

  expandExtraText() {
    this.extraTextExpanded = !this.extraTextExpanded
    this._cd.detectChanges()
    if (this.extraTextExpanded && this.selitysRef) {
      setTimeout(() => {
        this.selitysRef?.nativeElement?.scrollIntoView({ behavior: 'smooth' }, 10)
      })
    }
  }

  private _initializePickerSubtypes(): void {
    const firstMissingIban = this._data?.dialogToShowMetadata?.ibansMissing?.[0]
    if (!firstMissingIban) {
      return
    }
    const subtypes = this._enableBankingDialogService.getBankSubtypesForIban(firstMissingIban, this._data?.bankSubtypes)
    this.currentEbSubtypes = subtypes
    // Auto-select when there's no real choice.
    if (subtypes.length === 1) {
      this.selectedEbSubtype.set(this._enableBankingDialogService.convertToSelectedEbSubtype(subtypes[0]))
      asetaArvo(this.lomake.ebSubtypeName, subtypes[0].name)
      if (subtypes[0].psuTypes.length === 1) {
        asetaArvo(this.lomake.ebPsuType, subtypes[0].psuTypes[0])
      }
    }
  }

  private _initializeTexts(): void {
    const hasWorkingConnections = this._data?.dialogToShowMetadata?.ibansOk?.length > 0
    const hadPreviousConnection = this._hadPreviousConnection(this._data?.dialogToShowMetadata?.connectionLost)

    if (hasWorkingConnections || hadPreviousConnection) {
      this.valtuutusTitle = this._translationService.lokalisoi('eb-login-required.on-tileja.yhteys-vanhenemassa')
      this.valtuutusStartText = this._translationService.lokalisoi('eb-login-required.on-tileja.yhteys-ei-ole-aktiivinen')
      this.valtuutusEndText = this._translationService.lokalisoi('eb-login-required.on-tileja.valtuutuksen-antaminen')
    } else {
      this.valtuutusTitle = this._translationService.lokalisoi('eb-login-required.ei-tileja.yhdista-yrityksesi-pankkitili')
      this.valtuutusStartText = this._translationService.lokalisoi('eb-login-required.ei-tileja.palvelun-kayto-edellyttaa')
      this.valtuutusEndText = ''
    }
  }

  private _hadPreviousConnection(connectionLost: DialogToShowMetadata['connectionLost']): boolean {
    if (!connectionLost) {
      return false
    }
    const sixHoursAgo = this._dateService.lisaaMinuutteja(new Date(), 6 * -60)
    return connectionLost < this._dateService.dateToNumberTimestamp(sixHoursAgo)
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }
}
